import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const ACCENT = { wood: "#B5793F", mint: "#23C795", blue: "#19B5DE", ink: "#262016", warm: "#B5793F" };

// Renders **bold** markdown fragments inline as <strong>.
function renderInline(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#262016]">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

/**
 * Case card — full-width company/client headline layout.
 * The client is rendered as the big italic bronze headline (Fraunces).
 * The context (role/date/subtitle) sits underneath in small caps.
 * Case body flows edge-to-edge below.
 */
export function CaseCard({ content, delay = 0, accent = "ink", testId }) {
  const isPlaceholder = typeof content === "string" || content.isPlaceholder;
  const placeholderLabel = typeof content === "string" ? content : content.title;

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className="w-full border-b border-[#26201618] last:border-b-0"
      style={{ padding: "36px 32px" }}
      data-testid={testId}
    >
      {isPlaceholder ? (
        <div className="text-center py-6">
          <p className="rp-eyebrow mb-2">Case study slot</p>
          <p className="rp-display text-lg" style={{ color: ACCENT[accent] }}>{placeholderLabel}</p>
          <p className="text-xs text-[#8A7E6B] mt-2 italic">awaiting content from Rosh</p>
        </div>
      ) : (
        <div>
          {/* Headline — company / client name in Fraunces italic bronze */}
          <h4
            className="rp-display rp-italic font-medium leading-[1.0] tracking-tight mb-3"
            style={{
              color: ACCENT[accent],
              fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
              textWrap: "balance",
            }}
          >
            {content.eyebrow}
          </h4>
          {/* Subtitle — role / date / context */}
          {content.title && content.title !== content.eyebrow && (
            <p
              className="rp-display text-lg md:text-xl text-[#262016] leading-snug mb-6"
              style={{ textWrap: "balance" }}
            >
              {content.title}
            </p>
          )}
          {/* Body — full width, edge to edge */}
          <div className="space-y-3 text-[15px] text-[#5C5042] leading-relaxed" style={{ textWrap: "pretty" }}>
            {content.challenge && (
              <p>
                <strong className="text-[#262016] font-semibold">The Challenge/Opportunity: </strong>
                {renderInline(content.challenge)}
              </p>
            )}
            {content.didWhat && (
              Array.isArray(content.didWhat) && content.didWhat.length > 0 ? (
                <div>
                  <p className="mb-1.5">
                    <strong className="text-[#262016] font-semibold">What I Did:</strong>
                  </p>
                  <ul className="space-y-1.5 pl-0.5">
                    {content.didWhat.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span
                          className="mt-1.5 shrink-0 rounded-full"
                          style={{ background: ACCENT[accent], width: 5, height: 5 }}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{renderInline(b)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p>
                  <strong className="text-[#262016] font-semibold">What I Did: </strong>
                  {renderInline(content.didWhat)}
                </p>
              )
            )}
            {Array.isArray(content.impactBullets) && content.impactBullets.length > 0 && (
              <div>
                <p className="mb-1.5">
                  <strong className="text-[#262016] font-semibold">Impact:</strong>
                </p>
                <ul className="space-y-1.5 pl-0.5">
                  {content.impactBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span
                        className="mt-1.5 shrink-0 rounded-full"
                        style={{ background: ACCENT[accent], width: 5, height: 5 }}
                        aria-hidden="true"
                      />
                      <span className="flex-1">{renderInline(b)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.article>
  );
}

export default function PathSection({ path, isLast, onNext }) {
  return (
    <section
      id={`path-${path.id}`}
      className="rp-section min-h-screen flex flex-col justify-center scroll-mt-16"
      data-testid={`path-${path.id}`}
    >
      <div className="max-w-6xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7 }}
          className="mb-10 md:mb-14"
        >
          <p className="rp-eyebrow mb-3" style={{ color: ACCENT[path.accent] || ACCENT.ink }}>
            {path.eyebrow || `Path · ${path.label}`}
          </p>
          <h2 className="rp-display text-4xl md:text-5xl lg:text-6xl font-light text-[#262016] leading-tight mb-4">
            {path.label}
          </h2>
          <p className="rp-display rp-italic text-lg md:text-xl text-[#5C5042]" style={{ textWrap: 'pretty' }}>
            {path.intro}
          </p>
        </motion.div>

        {/* Cases grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {path.cases?.map((c, i) => (
            <CaseCard key={i} content={c} delay={0.1 + i * 0.08} accent={path.accent} testId={`case-${path.id}-${i}`} />
          ))}
        </div>

        {/* Design Portfolio: "Know more" button */}
        {path.knowMore && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex justify-center"
          >
            <a
              href={path.knowMore.href || "/design-portfolio"}
              data-testid={`${path.id}-know-more-btn`}
              className="group inline-flex items-center gap-3 rounded-full px-8 py-4 md:px-10 md:py-5 rp-display text-lg md:text-xl font-semibold tracking-tight text-[#F6F1E5] transition-transform hover:-translate-y-0.5 shadow-[0_18px_40px_-14px_rgba(38,32,22,0.55)]"
              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}
            >
              <span>{path.knowMore.label || "View Full Portfolio"}</span>
              <ArrowUpRight
                size={22}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                style={{ color: "#EAB16A" }}
              />
            </a>
          </motion.div>
        )}

        {/* Sequential Next */}
        {!isLast && (
          <div className="mt-12 flex justify-end">
            <button
              onClick={onNext}
              data-testid={`path-next-${path.id}`}
              className="rp-btn rp-btn-ghost text-sm group"
            >
              Next <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
